/**
 * EditBookingModal - Modal for editing booking details
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  Space,
  message,
  DatePicker,
  Select,
  Input,
  Row,
  Col,
  Alert,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  updateBooking,
  getAvailableTeams,
} from "../../quotes/services/bookingService";
import { getQuoteById } from "../../quotes/services/quoteService";
import { getLead } from "../../leads/services/leadService";
import ProviderDriverAssignmentFields from "../../drivers/components/ProviderDriverAssignmentFields";
import { providerFromAssignedTeam } from "../../drivers/utils/providerDriverAssignment";
import { AddressValidationService } from "@/shared/services/addressValidationService";
import { buildBookingNotesFromLead } from "@/shared/utils/leadAccessibility";
import ResponsiveDrawer from "@/shared/components/ResponsiveDrawer.jsx";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const EditBookingModal = ({ booking, order, open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [quoteData, setQuoteData] = useState(null);
  const [addressErrors, setAddressErrors] = useState({
    pickup: null,
    delivery: null,
  });

  // Extract booking data - handle both { order: {...}, booking: {...} } and direct booking object
  const bookingData = booking?.booking || booking;
  // Ensure we have order object - use order prop or extract from booking
  const orderObj = order || booking?.order;

  useEffect(() => {
    if (open && bookingData) {
      initializeForm();
    }
  }, [open, booking, form, order]);

  const initializeForm = async () => {
    const currentDriverId =
      bookingData.assignedTeam?.primaryDriver?.driverId || null;

    let notes = {
      stairs: bookingData.notes?.stairs || "",
      accessRules: bookingData.notes?.accessRules || "",
      fragileItems: bookingData.notes?.fragileItems || "",
      gateCode: bookingData.notes?.gateCode || "",
      estateName: bookingData.notes?.estateName || "",
      contactOnDay: bookingData.notes?.contactOnDay || "",
    };

    let quote = null;
    if (orderObj?.quoteId) {
      quote = await loadQuoteData(orderObj.quoteId);
    }

    const leadId = quote?.leadId;
    if (leadId) {
      try {
        const leadResult = await getLead(leadId);
        if (leadResult.success && leadResult.data) {
          notes = buildBookingNotesFromLead(leadResult.data, notes);
        }
      } catch (error) {
        console.error(
          "EditBookingModal: Error loading lead accessibility for booking notes:",
          error
        );
      }
    }

    const initialValues = {
      moveDate: bookingData.moveDate ? dayjs(bookingData.moveDate) : null,
      pickupTimeWindow:
        bookingData.pickupTimeWindow?.type ||
        bookingData.pickupTimeWindow ||
        null,
      teamId: bookingData.assignedTeam?.teamId || null,
      driverId: currentDriverId,
      pickupAddress: orderObj?.pickup?.address || "",
      deliveryAddress: orderObj?.delivery?.address || "",
      notes,
    };

    form.setFieldsValue(initialValues);
    setOriginalValues(JSON.parse(JSON.stringify(initialValues)));
    setHasChanges(false);

    if (initialValues.moveDate) {
      loadAvailableTeams(initialValues.moveDate, initialValues.pickupTimeWindow);
    }
  };

  const loadQuoteData = async (quoteId) => {
    try {
      const result = await getQuoteById(quoteId);
      let quote = null;
      if (result && result.success && result.data) {
        quote = result.data;
      } else if (result && result.data) {
        quote = result.data;
      }

      if (quote) {
        setQuoteData(quote);
      }

      return quote;
    } catch (error) {
      return null;
    }
  };

  const loadAvailableTeams = async (moveDate, timeWindow) => {
    if (!moveDate) return;

    setLoadingTeams(true);
    try {
      const loadType =
        quoteData?.loadType || orderObj?.loadType || "medium_load";
      const result = await getAvailableTeams({
        moveDate: moveDate.format("YYYY-MM-DD"),
        loadType: loadType,
        timeWindow: timeWindow || "flexible",
      });

      if (result.success) {
        setTeams(result.data || []);
      } else {
        setTeams([]);
      }
    } catch (error) {
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    // Check if there are any changes
    const currentValues = form.getFieldsValue();
    const currentValuesStr = JSON.stringify(currentValues);
    const originalValuesStr = JSON.stringify(originalValues);
    const hasChanges = currentValuesStr !== originalValuesStr;
    setHasChanges(hasChanges);

    // Reload teams and drivers if moveDate or timeWindow changes
    if (changedValues.moveDate || changedValues.pickupTimeWindow) {
      const moveDate = allValues.moveDate || currentValues.moveDate;
      const timeWindow =
        allValues.pickupTimeWindow || currentValues.pickupTimeWindow;
      if (moveDate) {
        loadAvailableTeams(moveDate, timeWindow);
      }
    }

    if (changedValues.driverId) {
      form.setFieldsValue({ teamId: null });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Get current addresses from form values or original values
      const pickupAddress =
        values.pickupAddress?.trim() || originalValues.pickupAddress;
      const deliveryAddress =
        values.deliveryAddress?.trim() || originalValues.deliveryAddress;

      // Check if either address is being changed
      const pickupChanged =
        pickupAddress && pickupAddress !== originalValues.pickupAddress;
      const deliveryChanged =
        deliveryAddress && deliveryAddress !== originalValues.deliveryAddress;
      const anyAddressChanged = pickupChanged || deliveryChanged;

      // If any address is being updated, validate both addresses
      if (anyAddressChanged) {
        // Both addresses must be provided
        if (!pickupAddress || !deliveryAddress) {
          message.error(
            "Both pickup and delivery addresses are required when updating addresses"
          );
          setLoading(false);
          return;
        }

        // Validate address format using existing validation service
        const addressValidation =
          AddressValidationService.validateBookingAddresses(
            pickupAddress,
            deliveryAddress
          );

        if (!addressValidation.isValid) {
          setAddressErrors({
            pickup: addressValidation.pickup.isValid
              ? null
              : addressValidation.pickup.error,
            delivery: addressValidation.delivery.isValid
              ? null
              : addressValidation.delivery.error,
          });
          message.error(
            addressValidation.errors?.join("; ") || "Address validation failed"
          );
          setLoading(false);
          return;
        }

        // Clear errors if validation passes
        setAddressErrors({ pickup: null, delivery: null });
      }

      // Prepare update payload
      const updates = {
        moveDate: values.moveDate ? values.moveDate.format("YYYY-MM-DD") : null,
        pickupTimeWindow: values.pickupTimeWindow || null,
        teamId: values.teamId || null,
        driverId: values.driverId || null,
        notes: {
          stairs: values.notes?.stairs || "",
          accessRules: values.notes?.accessRules || "",
          fragileItems: values.notes?.fragileItems || "",
          gateCode: values.notes?.gateCode || "",
          estateName: values.notes?.estateName || "",
          contactOnDay: values.notes?.contactOnDay || "",
        },
      };

      // Include addresses if they are being updated (send both if either changed)
      if (anyAddressChanged) {
        updates.pickupAddress = pickupAddress;
        updates.deliveryAddress = deliveryAddress;
      }

      // Get orderId from order prop or booking.order.id
      // booking structure: { order: {...}, booking: {...} }
      const orderId = orderObj?.id;
      if (!orderId) {
        console.error("EditBookingModal: Order ID not found", {
          order,
          orderObj,
          booking,
          orderIdFromOrder: order?.id,
          orderIdFromBooking: booking?.order?.id,
        });
        message.error(
          "Order ID is required. Please refresh the page and try again."
        );
        setLoading(false);
        return;
      }

      const result = await updateBooking(orderId, updates);

      if (result.success) {
        message.success("Booking updated successfully");
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        message.error(result.error || "Failed to update booking");
      }
    } catch (error) {
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      message.error(error.message || "Failed to update booking");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: "Discard changes?",
        content: "You have unsaved changes. Are you sure you want to close?",
        onOk: () => {
          form.resetFields();
          setHasChanges(false);
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  return (
    <ResponsiveDrawer
      title={
        <Space>
          <EditOutlined />
          <span>Edit Booking</span>
        </Space>
      }
      open={open}
      onClose={handleCancel}
      desktopWidth={720}
      footer={
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button icon={<CloseOutlined />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={handleSave}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </Space>
        </div>
      }
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="moveDate"
              label="Move Date"
              rules={[{ required: true, message: "Move date is required" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={(current) =>
                  current && current < dayjs().startOf("day")
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="pickupTimeWindow"
              label="Pickup Time Window"
              rules={[{ required: true, message: "Time window is required" }]}
            >
              <Select placeholder="Select time window">
                <Option value="morning">Morning (8:00 - 12:00)</Option>
                <Option value="midday">Midday (12:00 - 14:00)</Option>
                <Option value="afternoon">Afternoon (14:00 - 18:00)</Option>
                <Option value="flexible">Flexible</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Address Fields */}
        <Form.Item
          name="pickupAddress"
          label="Pickup Address"
          help="Format: Street Number Street Name, Suburb, Town, Province (e.g., 11 Conrad Dr, Blairgowrie, Randburg, Johannesburg, Gauteng)"
          validateStatus={addressErrors.pickup ? "error" : ""}
        >
          <TextArea
            rows={2}
            placeholder="Enter pickup address (e.g., 11 Conrad Dr, Blairgowrie, Randburg, Johannesburg, Gauteng)"
          />
        </Form.Item>
        {addressErrors.pickup && (
          <Alert
            message={addressErrors.pickup}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            icon={<WarningOutlined />}
          />
        )}

        <Form.Item
          name="deliveryAddress"
          label="Delivery Address"
          help="Format: Street Number Street Name, Suburb, Town, Province (e.g., 11 Conrad Dr, Blairgowrie, Randburg, Johannesburg, Gauteng)"
          validateStatus={addressErrors.delivery ? "error" : ""}
        >
          <TextArea
            rows={2}
            placeholder="Enter delivery address (e.g., 11 Conrad Dr, Blairgowrie, Randburg, Johannesburg, Gauteng)"
          />
        </Form.Item>
        {addressErrors.delivery && (
          <Alert
            message={addressErrors.delivery}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            icon={<WarningOutlined />}
          />
        )}

        <ProviderDriverAssignmentFields
          form={form}
          resetKey={open}
          initialProvider={providerFromAssignedTeam(bookingData?.assignedTeam)}
          initialDriverId={
            bookingData?.assignedTeam?.primaryDriver?.driverId || null
          }
          onProviderChange={() => {
            form.setFieldsValue({ teamId: null });
          }}
        />

        <Form.Item
          name="teamId"
          label="Assigned Team"
          tooltip="Select a team for this booking. Teams are filtered based on move date and time window. Note: If you assign a driver above, the team will be automatically updated."
        >
          <Select
            placeholder="Select team (optional)"
            allowClear
            loading={loadingTeams}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {teams.map((team) => (
              <Option key={team.id} value={team.id}>
                {team.name} ({team.vehicleType?.toUpperCase() || "N/A"})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Special Notes">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name={["notes", "stairs"]} label="Stairs" noStyle>
                <Input placeholder="Number of stairs" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name={["notes", "gateCode"]} label="Gate Code" noStyle>
                <Input placeholder="Gate code" />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item name={["notes", "estateName"]} label="Estate Name">
          <Input placeholder="Estate/Complex name" />
        </Form.Item>

        <Form.Item name={["notes", "accessRules"]} label="Access Rules">
          <TextArea rows={3} placeholder="Special access instructions" />
        </Form.Item>

        <Form.Item name={["notes", "fragileItems"]} label="Fragile Items">
          <TextArea rows={3} placeholder="Details about fragile items" />
        </Form.Item>

        <Form.Item name={["notes", "contactOnDay"]} label="Contact on Day">
          <Input placeholder="Contact person on move day" />
        </Form.Item>
      </Form>
    </ResponsiveDrawer>
  );
};

export default EditBookingModal;
